import { useCallback } from 'react';
import { nanoid } from 'nanoid';
import { useSetRecoilState } from 'recoil';
import {
  insertedTemplateMetricsState,
  InsertedTemplateDataSource,
  templateInsertRequestState,
} from 'domains/reporter/Reporter/state';

type SetTemplateInsertionParams = {
  templateId: string;
  source: InsertedTemplateDataSource;
  worklistItemSmid: string;
};

export const useTemplateInsertionState = () => {
  const setInsertedTemplateMetricsState = useSetRecoilState(insertedTemplateMetricsState);
  const setTemplateInsertRequestState = useSetRecoilState(templateInsertRequestState);

  const setTemplateInsertion = useCallback(
    ({ templateId, source, worklistItemSmid }: SetTemplateInsertionParams) => {
      setInsertedTemplateMetricsState({
        templateId,
        source,
        hasConfirmationDialogTriggered: false,
      });
      setTemplateInsertRequestState({
        requestId: nanoid(),
        templateId,
        source,
        worklistItemSmid,
      });
    },
    [setInsertedTemplateMetricsState, setTemplateInsertRequestState]
  );

  const markTemplateInsertionConfirmationDialogTriggered = useCallback(() => {
    setInsertedTemplateMetricsState((prev) => {
      if (prev == null) {
        return prev;
      }
      return { ...prev, hasConfirmationDialogTriggered: true };
    });
  }, [setInsertedTemplateMetricsState]);

  const clearTemplateInsertRequestIfMatches = useCallback(
    (requestId: string) => {
      setTemplateInsertRequestState((previousState) =>
        previousState?.requestId === requestId ? null : previousState
      );
    },
    [setTemplateInsertRequestState]
  );

  return {
    setTemplateInsertion,
    markTemplateInsertionConfirmationDialogTriggered,
    clearTemplateInsertRequestIfMatches,
  };
};
